/* eslint-disable react/jsx-sort-props */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Divider, Grid } from '@mui/joy';
import Alert from '@mui/joy/Alert';
import Box from '@mui/joy/Box';
import Card from '@mui/joy/Card';
import CircularProgress from '@mui/joy/CircularProgress';
import Typography from '@mui/joy/Typography';

import BatteryBarLevel0 from '../assets/images/BatteryBarLevel0.svg?react';
import BatteryBarLevel2 from '../assets/images/BatteryBarLevel2.svg?react';
import BatteryBarLevel4 from '../assets/images/BatteryBarLevel4.svg?react';
import BatteryBarLevel6 from '../assets/images/BatteryBarLevel6.svg?react';
import DronePreview from '../assets/images/DronePreview.svg?react';
import SignalCellularStrong from '../assets/images/SignalCellularStrong.svg?react';
import BreadcrumbLayout from '../components/layout/BreadcrumbLayout';
import ResponsiveLayout from '../components/layout/ResponsiveLayout';
import { DroneStatusTag } from '../general-utilities/droneUtils';
import { BreadcrumbItem } from '../models/BreadcrumbItem';
import { DeviceHeartBeat } from '../models/Device';
import { DeviceService } from '../services/DeviceService';

type DeviceInfo = {
  id: string;
  name?: string;
  device_type?: string | null;
  device_type_name?: string;
  description?: string;
  device_uuid: string;
  serial_number: string;
  organization?: number | null;
  remote_id_number?: string;
  faa_reg_number?: string;
  is_active?: boolean;
  release_date?: string;
  created_at?: string;
  updated_at?: string;
  heart_beat?: DeviceHeartBeat;
};

const DeviceDetails = () => {
  const { t } = useTranslation();
  const { deviceId } = useParams();
  const [device, setDevice] = useState<DeviceInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const getDevice = async () => {
      if (!deviceId) return;
      setLoading(true);
      try {
        const deviceResult = await DeviceService.getDeviceById(deviceId);
        if (deviceResult?.data) {
          const deviceDetail = deviceResult.data;
          setDevice({
            id: deviceDetail.id!,
            name: deviceDetail.name || '-',
            device_type: deviceDetail.device_type?.id || null,
            description: deviceDetail.description,
            device_uuid: deviceDetail.device_uuid,
            serial_number: deviceDetail.serial_number,
            faa_reg_number: deviceDetail.faa_reg_number || '-',
            remote_id_number: deviceDetail.remote_id_number || '-',
            heart_beat: deviceDetail.heart_beat as DeviceHeartBeat | undefined
          });
        } else {
          setDevice(null);
        }
      } catch {
        setError(t('devices.error_fetch'));
      } finally {
        setLoading(false);
      }
    };
    getDevice();
  }, [deviceId, t]);

  if (!deviceId)
    return (
      <Alert color='danger' variant='soft'>
        {t('devices.name')} {t('not_found')}
      </Alert>
    );
  if (loading)
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight={200}>
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Alert color='danger' variant='soft'>
        {error}
      </Alert>
    );
  if (!device)
    return (
      <Alert color='danger' variant='soft'>
        {t('devices.name')} {deviceId} {t('not_found')}
      </Alert>
    );

  // Setup breadcrumbs
  const breadcrumbItems: BreadcrumbItem[] = [
    { text: t('devices.title'), link: '/devices' },
    { text: device.device_uuid }
  ];

  const getBatteryInfo = () => {
    if (!device.heart_beat?.battery_percentage) {
      return <Box className='flex items-center'>-</Box>;
    }

    return (
      <Box className='flex items-center'>
        {' '}
        {/* TODO add real battery info when embeded team has this data */}
        <Typography>{`${device.heart_beat?.battery_percentage}%`}</Typography>
        {
          [
            <BatteryBarLevel0 style={{ marginLeft: '6px' }} />,
            <BatteryBarLevel2 style={{ marginLeft: '6px' }} />,
            <BatteryBarLevel4 style={{ marginLeft: '6px' }} />,
            <BatteryBarLevel6 style={{ marginLeft: '6px' }} />
          ][[25, 50, 75, 100].findIndex((val) => val >= (device.heart_beat?.battery_percentage as number))]
        }
      </Box>
    );
  };

  return (
    <ResponsiveLayout>
      <BreadcrumbLayout breadcrumbItems={breadcrumbItems}>
        <Box className='flex items-start'>
          <Typography level='h4' className='pl-3 pb-3 pr-2'>
            {device.name}
          </Typography>
          <DroneStatusTag last={device.heart_beat?.timestamp ? new Date(device.heart_beat.timestamp) : undefined} />
        </Box>
        <Grid container spacing={2} alignItems='stretch'>
          <Grid xs={8}>
            <Card>
              <Box className='flex'>
                <DronePreview className='mr-4' />
                <Box className='flex flex-col flex-1 justify-between'>
                  <Box>
                    <Grid container className='flex'>
                      <Grid xs={3.5}>
                        <Typography sx={{ color: 'text.tertiary' }}>{t('devices.serial_number')}</Typography>
                      </Grid>
                      <Grid xs={3.5}>
                        <Typography sx={{ color: 'text.tertiary' }}>{t('devices.model')}</Typography>
                      </Grid>
                      <Grid xs={5}>
                        <Typography sx={{ color: 'text.tertiary' }}>{t('devices.faa_registration_number')}</Typography>
                      </Grid>
                    </Grid>
                    <Divider sx={{ margin: '6px 0' }} />
                    <Grid container className='flex-1'>
                      <Grid xs={3.5}>
                        <Typography>{device.serial_number}</Typography>
                      </Grid>
                      <Grid xs={3.5}>
                        <Typography>
                          {device.device_type_name ||
                            (device.device_type && typeof device.device_type === 'object'
                              ? (device.device_type as any).name || (device.device_type as any).model_name || '-'
                              : device.device_type || '-')}
                        </Typography>
                      </Grid>
                      <Grid xs={5}>
                        <Typography>{device.faa_reg_number}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box>
                    <Grid container className='flex'>
                      <Grid xs={3.5}>
                        <Typography sx={{ color: 'text.tertiary' }}>{t('devices.remote_id')}</Typography>
                      </Grid>
                      <Grid xs={3.5}>
                        <Typography sx={{ color: 'text.tertiary' }}>{t('devices.last_heartbeat.header')}</Typography>
                      </Grid>
                      <Grid xs={2.5}>
                        <Typography sx={{ color: 'text.tertiary' }}>{t('devices.battery')}</Typography>
                      </Grid>
                      <Grid xs={2.5}>
                        <Typography sx={{ color: 'text.tertiary' }}>{t('devices.connection.header')}</Typography>
                      </Grid>
                    </Grid>
                    <Divider sx={{ margin: '6px 0' }} />
                    <Grid container className='flex-1'>
                      <Grid xs={3.5}>
                        <Typography>{device.remote_id_number}</Typography>
                      </Grid>
                      <Grid xs={3.5}>
                        <Typography>
                          {device.heart_beat?.timestamp
                            ? new Date(device.heart_beat.timestamp).toLocaleString()
                            : t('devices.last_heartbeat.none')}
                        </Typography>
                      </Grid>
                      <Grid xs={2.5}>{getBatteryInfo()}</Grid>
                      <Grid xs={2.5}>
                        <Box className='flex items-center'>
                          <SignalCellularStrong />
                          <Typography className='pl-2' sx={{ color: 'success.outlined.color' }}>
                            {t('devices.connection.strong')}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid xs={4} className='flex flex-col'>
            <Card className='flex-1'>
              <Typography sx={{ color: 'text.tertiary' }}>Software</Typography>
              <Typography>-</Typography>
              {/* TODO: show software information when embedded team has this data */}
            </Card>
            <Card className='flex-1 mt-4'>
              <Typography sx={{ color: 'text.tertiary' }}>Error</Typography>
              <Typography>-</Typography>
            </Card>
          </Grid>
        </Grid>
      </BreadcrumbLayout>
    </ResponsiveLayout>
  );
};

export default DeviceDetails;
